import { Alert, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef, useState } from 'react';
import {
  ImperativePanelGroupHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import ItemBuilder from '../../features/item-builder/ItemBuilder';
import useAIMessaging from '../../features/messaging/useAIMessaging.hooks';
import useHypothesisFrames from '../../hooks/useHypothesisFrames.hooks';
import useAgentCartSync from '../../hooks/useAgentCartSync.hooks';
import useExpressTimer from '../../hooks/useExpressTimer.hooks';
import useUnresolvedItemsSummary from '../../hooks/useUnresolvedItemsSummary.hooks';
import useSessionStatus from '../../hooks/sessionStatus/useSessionStatus.hooks';
import useWebSocketAlert from '../../hooks/useWebSocketAlert.hooks';
import {
  considerTimePeriods,
  considerTimePeriodsForUnifiedMenu,
} from '../../reducers/menuSlice';
import { selectCartModality } from '../../selectors/cart';
import {
  selectActiveRestaurantTimezone,
  selectedStageSelector,
} from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { ICartItemRefs, SetCartItemRef } from '../../types';
import Colors from '../../utils/color';
import { TIME_PERIOD_CHECK_INTERVAL } from '../../utils/constants';
import { getMenuUnificationFeatureFlag } from '../../utils/session-storage';
import { selectFeatureFlagFinalOrderValidation } from '../../redux/features/config/config.selector';
import { useTaskLoadingMetricsContext } from '../../hooks/taskLoadingMetrics/TaskLoadingMetricsContext';
import AIStatusBar from '../AIStatusBar';
import AISuggestions from '../AISuggestions';
import FinalOrderBackdrop from '../FinalOrderBackdrop';
import TaskLoadingMetricsDisplay from '../TaskLoadingMetricsDisplay';
import { useLayoutPreference } from '../Layout/LayoutPreferenceContext';
import LayoutPreferenceDialog, {
  DEFAULT_PANE_SIZES,
  MAX_PANE_SIZE,
  MIN_PANE_SIZE,
} from '../Layout/LayoutPreferenceDialog';
import AgentCartCard from './AgentCartCard';
import BottomActionBar from './BottomActionBar';
import BannerCard from './BannerCard';
import LiveTranscriptCard from './LiveTranscriptCard';
import ReminderCard from './ReminderCard';
import useTranscriptExpand from './useTranscriptExpand.hooks';

// Space to leave between the expanded transcript overlay and the right cart
// pane: the resize handle (spacing(1)) + the row's right padding (spacing(1)).
const OVERLAY_RIGHT_GAP = 16;

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  page: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.porcelain,
  },
  topRow: {
    flex: 1,
    minHeight: 0,
    // Horizontal padding keeps the panes off the screen edges; the PanelGroup
    // fills this box. Positioning context for the Live Transcript takeover.
    padding: `0 ${spacing(1)}`,
    position: 'relative',
  },
  // Draggable divider between panes — occupies the old inter-pane gap and shows
  // a grabber line that highlights on hover.
  resizeHandle: {
    width: spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    '&::after': {
      content: '""',
      width: 2,
      height: spacing(4),
      borderRadius: 1,
      backgroundColor: Colors.alto,
      transition: 'background-color 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
    },
  },
  leftPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Left card gets top space; only the center attaches to the header.
    paddingTop: spacing(1),
  },
  // Single left card: transcript section + reminder section + AI status bar,
  // each an internal white card under its heading (mirrors the mockup).
  leftCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1.5),
    backgroundColor: '#F9FAFB',
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: spacing(1),
    padding: spacing(1.5),
  },
  // AI-supervised takeover: the whole left card lifts out and overlays the
  // left + center area (stops before the right Agent Cart), so the transcript
  // gets the room while the reminder + AI status sections stay visible. The
  // `right` offset is set inline from the measured right-pane width so it stays
  // correct after the panes are resized.
  leftCardExpanded: {
    position: 'absolute',
    top: spacing(1),
    left: spacing(1),
    bottom: 0,
    zIndex: 5,
  },
  // AI status bar slot. flexShrink:0 stops the flex transcript above from
  // squeezing it. Negative margins cancel the left card's padding so the bar
  // attaches flush to the card's bottom corners. All AIStatusBar overrides are
  // scoped here so the shared component (and its tests) stay untouched.
  statusSlot: {
    flexShrink: 0,
    margin: spacing(0, -1.5, -1.5),
    '& [data-testid="ai-status-bar"]': {
      // Size to content up to ~2 lines, then scroll (was a fixed 48px).
      height: 'auto',
      maxHeight: spacing(7),
      overflowY: 'auto',
      padding: spacing(1),
      borderBottomLeftRadius: spacing(1),
      borderBottomRightRadius: spacing(1),
    },
    '& [data-testid="ai-status-bar"] > div': {
      // Match the transcript text size (0.75rem / 12px) so the left pane is
      // consistent (the shared component's own style sets 1rem / 30px).
      height: 'auto',
      fontSize: '0.75rem',
      lineHeight: 1.4,
      // The shared component lays the label + message out as a flex row, which
      // pins "AUTO AI:" in its own left column and confines the message to a
      // narrow column on the right — wasting the space below the short label in
      // this 360px pane. Flow them as inline text instead so the message wraps
      // across the full bar width (lines after the first flow back under the
      // label).
      display: 'block',
    },
    '& [data-testid="ai-status-bar"] > div > div': {
      display: 'inline',
    },
  },
  middlePane: {
    height: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Match the left/right panes' top gap so the center card doesn't attach to
    // the header.
    paddingTop: spacing(1),
    // Anchor for the absolutely-positioned WebSocket connection alerts.
    position: 'relative',
  },
  alertClass: {
    position: 'absolute',
    left: spacing(2),
    right: spacing(2),
    zIndex: 200,
    alignItems: 'center',
  },
  // Single center card: Current Step section pinned at the top, option groups
  // scrolling below it.
  centerCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.white,
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: spacing(1),
    overflow: 'hidden',
  },
  optionScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: spacing(0, 1.5, 1.5, 1.5),
    // ItemBuilder keeps its original spacing(7) top padding (it makes room for
    // the Banner overlay in the OLD layout). Here BannerCard is a separate flex
    // child above, so drop that top gap to keep the option groups flush.
    '& #itemBuilder': {
      paddingTop: 0,
    },
  },
  rightPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    paddingTop: spacing(1),
  },
}));

/**
 * MainPageLayout — new HITL terminal layout (replacement for MainContent).
 *
 * The three panes are resizable (react-resizable-panels); sizes persist per
 * agent via `autoSaveId`, and the "Layout preference" dialog drives them
 * imperatively. The top app bar (Header) lives outside this component (App.tsx).
 */
const MainPageLayout = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const cartItemRefs = useRef<ICartItemRefs>({});
  const setCartItemRef: SetCartItemRef = (cartItemId, ref) => {
    cartItemRefs.current[cartItemId] = ref;
  };

  // AI auto-mode status bar visibility.
  const { isAIActive, isAIAutoMode } = useAIMessaging();
  const showAIStatus = isAIActive && isAIAutoMode;

  // Left-card takeover expansion (transcript + reminder + AI status grow as one).
  const { isExpanded, setIsExpanded } = useTranscriptExpand();

  // Resizable-pane wiring: a per-agent storage key, the live sizes (kept in sync
  // for the preference dialog), and the imperative handle the dialog writes to.
  const userProfile = useAppSelector(selectUserProfile);
  const paneStorageKey = `hitl-console-panes:${
    userProfile?.username || userProfile?.email || 'default'
  }`;
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const [liveSizes, setLiveSizes] = useState<number[]>(DEFAULT_PANE_SIZES);

  // The takeover overlay stops before the (resizable) right cart pane, so track
  // its live pixel width and offset the overlay from it.
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const [rightPaneWidth, setRightPaneWidth] = useState<number>(360);
  useEffect(() => {
    const element = rightPaneRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setRightPaneWidth(width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Layout-preference dialog (opened from the header ProfileMenu via context).
  const { isOpen: isLayoutPrefOpen, close: closeLayoutPref } =
    useLayoutPreference();
  const applyPaneSizes = (sizes: number[]) =>
    panelGroupRef.current?.setLayout(sizes);
  const resetPaneSizes = () =>
    panelGroupRef.current?.setLayout(DEFAULT_PANE_SIZES);

  // Task-loading metrics readout.
  const { lastSentMetrics } = useTaskLoadingMetricsContext();

  // Time-period menu rollover inputs.
  const timezone = useAppSelector(selectActiveRestaurantTimezone);
  const stage = useAppSelector(selectedStageSelector);
  const modality = useAppSelector(selectCartModality);

  // WebSocket connection alert state.
  const eventWSConnectionMessage = useAppSelector(
    (state) => state.messages.eventWSConnectionMessage
  );
  const audioWSConnectionMessage = useAppSelector(
    (state) => state.messages.audioWSConnectionMessage
  );
  const eventWSConnectionSuccess = useAppSelector(
    (state) => state.messages.eventWSConnectionSuccess
  );
  const audioWSConnectionSuccess = useAppSelector(
    (state) => state.messages.audioWSConnectionSuccess
  );

  const FEATURE_FLAG_FINAL_ORDER_VALIDATION = useAppSelector(
    selectFeatureFlagFinalOrderValidation
  );

  const { showAlert: showEventAlert, handleCloseAlert: handleCloseEventAlert } =
    useWebSocketAlert({
      wsConnectionMessage: eventWSConnectionMessage,
      wsConnectionSuccess: eventWSConnectionSuccess,
    });

  const { showAlert: showAudioAlert, handleCloseAlert: handleCloseAudioAlert } =
    useWebSocketAlert({
      wsConnectionMessage: audioWSConnectionMessage,
      wsConnectionSuccess: audioWSConnectionSuccess,
    });

  // Poll the clock to roll the menu over between day-parts (breakfast/lunch/…).
  useEffect(() => {
    if (timezone) {
      const timer = setInterval(() => {
        if (getMenuUnificationFeatureFlag()) {
          dispatch(considerTimePeriodsForUnifiedMenu({ timezone }));
        } else {
          dispatch(considerTimePeriods({ modality, timezone, stage }));
        }
      }, TIME_PERIOD_CHECK_INTERVAL);
      return () => {
        clearInterval(timer);
      };
    }
  }, [timezone, dispatch, modality, stage]);

  // Core call-handling side effects (cart rebuild from hypotheses, unresolved
  // items summary, session lifecycle). These run for their effects only.
  useHypothesisFrames();
  useUnresolvedItemsSummary();
  useSessionStatus();
  useAgentCartSync();
  useExpressTimer();

  return (
    <div id="main-page-layout" className={classes.page}>
      <div className={classes.topRow}>
        <PanelGroup
          direction="horizontal"
          autoSaveId={paneStorageKey}
          ref={panelGroupRef}
          onLayout={setLiveSizes}
        >
          {/* LEFT SIDEBAR: one card — transcript + reminder sections + AI status.
              overflow:visible while expanded lets the takeover overlay escape. */}
          <Panel
            id="left"
            order={1}
            defaultSize={DEFAULT_PANE_SIZES[0]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
            style={{ overflow: isExpanded ? 'visible' : 'hidden' }}
          >
            <div className={classes.leftPane}>
              <div
                className={`${classes.leftCard} ${
                  isExpanded ? classes.leftCardExpanded : ''
                }`}
                style={
                  isExpanded
                    ? {
                        right: `calc(${rightPaneWidth}px + ${OVERLAY_RIGHT_GAP}px)`,
                      }
                    : undefined
                }
              >
                <LiveTranscriptCard
                  isExpanded={isExpanded}
                  onCollapse={() => setIsExpanded(false)}
                />
                <AISuggestions />
                <ReminderCard />
                {showAIStatus && (
                  <div className={classes.statusSlot}>
                    <AIStatusBar />
                  </div>
                )}
              </div>
            </div>
          </Panel>

          <PanelResizeHandle className={classes.resizeHandle} />

          {/* MIDDLE: one card — current step (top) + option groups (ItemBuilder) */}
          <Panel
            id="middle"
            order={2}
            defaultSize={DEFAULT_PANE_SIZES[1]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
          >
            <div className={classes.middlePane}>
              <TaskLoadingMetricsDisplay metrics={lastSentMetrics} />
              {showEventAlert && eventWSConnectionMessage && (
                <Alert
                  variant="filled"
                  severity={eventWSConnectionSuccess ? 'success' : 'error'}
                  className={classes.alertClass}
                  onClose={
                    eventWSConnectionSuccess ? handleCloseEventAlert : undefined
                  }
                >
                  Event WebSocket: {eventWSConnectionMessage}
                </Alert>
              )}
              {showAudioAlert && audioWSConnectionMessage && (
                <Alert
                  variant="filled"
                  severity={audioWSConnectionSuccess ? 'success' : 'error'}
                  className={classes.alertClass}
                  onClose={
                    audioWSConnectionSuccess ? handleCloseAudioAlert : undefined
                  }
                  sx={{
                    top:
                      showEventAlert && eventWSConnectionMessage
                        ? '72px'
                        : undefined,
                  }}
                >
                  Audio WebSocket: {audioWSConnectionMessage}
                </Alert>
              )}
              <div className={classes.centerCard}>
                <BannerCard />
                <div className={classes.optionScroll}>
                  <ItemBuilder setCartItemRef={setCartItemRef} />
                </div>
              </div>
            </div>
          </Panel>

          <PanelResizeHandle className={classes.resizeHandle} />

          {/* RIGHT: agent cart + totals + order actions */}
          <Panel
            id="right"
            order={3}
            defaultSize={DEFAULT_PANE_SIZES[2]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
          >
            <div className={classes.rightPane} ref={rightPaneRef}>
              <AgentCartCard />
            </div>
          </Panel>
        </PanelGroup>
      </div>

      {/* BOTTOM: quick response + conversation + order flow + escalation */}
      <BottomActionBar />

      <LayoutPreferenceDialog
        open={isLayoutPrefOpen}
        onClose={closeLayoutPref}
        sizes={liveSizes}
        onApply={applyPaneSizes}
        onReset={resetPaneSizes}
      />

      {FEATURE_FLAG_FINAL_ORDER_VALIDATION && <FinalOrderBackdrop />}
    </div>
  );
};

export default MainPageLayout;
